// PlayerVolume.tsx - Volume control with slider and mute button
import React from 'react';
import { styled } from 'styled-components';
import { Icon } from '@/styles/Icon';
import { calculateVolumeFromPosition } from './playerUtils';

interface PlayerVolumeProps {
  volume: number;
  isMuted: boolean;
  onVolumeChange: (volume: number) => void;
  onToggleMute: () => void;
}

export const PlayerVolume: React.FC<PlayerVolumeProps> = ({
  volume,
  isMuted,
  onVolumeChange,
  onToggleMute,
}) => {
  const volumePercent = isMuted ? 0 : volume * 100;

  const handleVolumeClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const newVolume = calculateVolumeFromPosition(e.clientX, rect);
    onVolumeChange(newVolume);
  };

  const handleVolumeDrag = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    const rect = e.currentTarget.getBoundingClientRect();

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const newVolume = calculateVolumeFromPosition(moveEvent.clientX, rect);
      onVolumeChange(newVolume);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <Container>
      <VolumeButton onClick={onToggleMute}>
        <Icon name={isMuted ? 'volumeMute' : 'volume'} size={24} />
      </VolumeButton>

      <VolumeBarContainer onClick={handleVolumeClick} onMouseDown={handleVolumeDrag}>
        <VolumeBar>
          <VolumeFill $width={volumePercent} />
          <VolumeHandle $left={volumePercent} />
        </VolumeBar>
      </VolumeBarContainer>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const VolumeButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: ${({ theme }) => theme.colors.playBtnGray};

  &:hover {
    opacity: 0.8;
  }
`;

const VolumeBarContainer = styled.div`
  width: 84px;
  height: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  position: relative;
`;

const VolumeBar = styled.div`
  position: relative;
  width: 100%;
  height: 4px;
  background: ${({ theme }) => theme.colors.bgGray[600]};
  border-radius: 5px;
`;

const VolumeFill = styled.div<{ $width: number }>`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: ${({ $width }) => $width}%;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: 5px;
`;

const VolumeHandle = styled.div<{ $left: number }>`
  position: absolute;
  left: ${({ $left }) => $left}%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  background: ${({ theme }) => theme.colors.bgWhite};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;

  &:hover {
    transform: translate(-50%, -50%) scale(1.2);
  }
`;
